import { useMemo } from 'react'
import type { Document } from './authoring'
import { useGetAuthoringComponentQuery } from './authoring'
import { fieldTarget, nodeAt } from '../model/command'
import type { LinkPath } from '../model/link'

/**
 * The node a selection names, and where an intent editing it goes.
 *
 * **Two screens select inside a document** — a page and a standalone
 * component — and the resolution is identical because it is the model's
 * rather than either screen's: a link path descends through inline
 * children, so an edit to one is a command against the document carrying
 * the path, and a path that crosses a link ends there because that
 * child's content is its own stream.
 *
 * That last case is the reason this is a hook rather than a call. A
 * linked child's document has not been fetched, and `nodeAt` will not
 * fetch — it is a pure read used while drawing. So the document is
 * loaded here and handed to it, and the query is declared always and
 * skipped when the selection did not cross a link, because hooks cannot
 * be conditional.
 */
export function useSelectedNode(tenant: string, document: Document | undefined, selection: LinkPath) {
  const target = useMemo(
    () => (document ? fieldTarget(document, selection) : undefined),
    [document, selection],
  )

  const linked = useGetAuthoringComponentQuery(
    { tenant, id: target?.id ?? '' },
    { skip: !target?.crossedLink || !target.id },
  )

  const node = document
    ? nodeAt(document, selection, (id) => (linked.data?.id === id ? linked.data : undefined))
    : undefined

  return { target, node, linked }
}
